import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Package, ExternalLink } from "lucide-react";
import { Section, ErrorNote, cents } from "@/components/connect/shared";

const EMPTY = { name: "", description: "", price: "" };

// Creates a product + default price on the host's connected account (Stripe-Account header on the server).
function ProductForm({ onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/connect/products", { ...f, price: Number(f.price), currency: "usd" });
      toast.success("Product created");
      setF(EMPTY);
      onCreated();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-3" data-testid="product-create-form">
      <div className="sm:col-span-2"><Label htmlFor="pn">Name</Label><Input id="pn" data-testid="product-name-input" value={f.name} onChange={set("name")} placeholder="Late checkout" required /></div>
      <div><Label htmlFor="pp">Price (USD)</Label><Input id="pp" type="number" min="1" step="0.01" data-testid="product-price-input" value={f.price} onChange={set("price")} required /></div>
      <div className="sm:col-span-3"><Label htmlFor="pd">Description</Label><Textarea id="pd" data-testid="product-description-input" value={f.description} onChange={set("description")} rows={2} /></div>
      <Button type="submit" disabled={busy} className="rounded-full sm:w-fit" data-testid="product-create-button">{busy ? "Saving…" : "Add product"}</Button>
    </form>
  );
}

const ProductRow = ({ p }) => (
  <li className="flex items-center justify-between gap-4 py-3" data-testid={`host-product-${p.id}`}>
    <div><p className="font-medium">{p.name}</p><p className="text-sm text-stone-500">{p.description}</p></div>
    <span className="font-semibold">{cents(p.unit_amount, p.currency || "usd")}</span>
  </li>
);

export function ProductsCard({ accountId }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["connect-products"], queryFn: () => api.get("/connect/products").then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["connect-products"] });
  return (
    <Section testid="connect-products-card" icon={Package} title="Your store products" subtitle="Sell extras like early check-in, airport pickup or welcome baskets.">
      <ProductForm onCreated={refresh} />
      {q.error && <div className="mt-4"><ErrorNote error={q.error} testid="products-error" /></div>}
      <ul className="mt-6 divide-y" data-testid="host-products-list">
        {(q.data || []).map((p) => <ProductRow key={p.id} p={p} />)}
        {q.data?.length === 0 && <li className="py-3 text-sm text-stone-500">No products yet.</li>}
      </ul>
      <Button asChild variant="outline" className="mt-4 rounded-full" data-testid="open-storefront-link">
        <Link to={`/store/${accountId}`}><ExternalLink className="mr-1 h-4 w-4" />View my storefront</Link>
      </Button>
    </Section>
  );
}
